import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DoubleTranslatedCustomRichText from '@/components/CustomRichText/DoubleTranslatedCustomRichText';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import Spinner from '@/components/UI/Spinner/Spinner';
import * as Constants from '@/helpers/constants';
import { getEditorId, removeParagraphTags, tabLowerCase } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { setHelpClassification } from '@/redux/Help/help.action';
import { getHelpClassification } from '@/redux/Help/help.reducer';
import administrationServices from '@/services/administrationServices';
import { ReduxHelp } from '@/types';
import styles from '../HelpText.module.scss';

interface ClassificationHelpTextProps {
  helpClassification: ReduxHelp;
  setClassification: (help: ReduxHelp) => void;
}

const ClassificationHelpText: React.FC<ClassificationHelpTextProps> = (props) => {
  const { t } = useTranslation();
  const tab = tabLowerCase(window.location.pathname.split('/')[4]);
  const [originalValues, setOriginalValues] = useState({});
  const { helpClassification, setClassification } = props;
  const isLoading = helpClassification && !helpClassification.CLASSIFICATION_TAB;
  const { addToast } = useToastContext();
  const CLASSIFICATION_TAB = 'CLASSIFICATION_TAB';
  const CLASSIFICATION_RELEVANCE = 'CLASSIFICATION_RELEVANCE';
  const CLASSIFICATION_CONSULTATION = 'CLASSIFICATION_CONSULTATION';
  const CLASSIFICATION_AFFECTS_CHINA = 'CLASSIFICATION_AFFECTS_CHINA';
  const CLASSIFICATION_CONSULTATION_VANS = 'CLASSIFICATION_CONSULTATION_VANS';
  const CLASSIFICATION_BLOCKING_RECOMENDATION = 'CLASSIFICATION_BLOCKING_RECOMENDATION';
  const CLASSIFICATION_DEVELOPMENT_VEHICLE_ASSESSMENT = 'CLASSIFICATION_DEVELOPMENT_VEHICLE_ASSESSMENT';

  const ID_GENERAL_CLASSIFICATION = 'general-classification';
  const ID_RELEVANCE_CLASSIFICATION = 'relevance-classification';
  const ID_CONSULTATION_CLASSIFICATION = 'consultation-classification';
  const ID_CHINA_CLASSIFICATION = 'china-classification';
  const ID_CONSULTATION_VANS_CLASSIFICATION = 'consultation-vans-classification';
  const ID_BLOCKING_RECOMENDATION_CLASSIFICATION = 'blocking-recomendation-classification';
  const ID_DEVELOPMENT_VEHICLE_ASSESSMENT_CLASSIFICATION = 'development-vehicle-assessment-classification';

  useEffect(() => {
    administrationServices.getHelp(tab)
      .then((data) => {
        const values = helpClassification && helpClassification.modified ? helpClassification : data;
        setClassification(values);
        setOriginalValues(values);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWysiwygChange = (lang, label, data) => {
    const propValues = { ...helpClassification[label] };
    const isModified = removeParagraphTags(data) !== removeParagraphTags(originalValues[label]?.[lang]);
    const newState = {
      ...helpClassification,
      [label]: {
        ...propValues,
        [lang]: data,
      },
      modified: isModified,
    };

    if (isModified) {
      setClassification(newState as unknown as ReduxHelp);
      setOriginalValues(newState);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <InfoMsg msg={t('administration.helpText.info')} />
          <AccordionPanel title={t('administration.helpText.generalText')}>
            <DoubleTranslatedCustomRichText
              id={ID_GENERAL_CLASSIFICATION}
              title={t('helptext')}
              initialValue={helpClassification?.CLASSIFICATION_TAB}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, CLASSIFICATION_TAB, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('classification.assessment')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_RELEVANCE_CLASSIFICATION}
              title={t('helptext')}
              initialValue={helpClassification?.CLASSIFICATION_RELEVANCE}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, CLASSIFICATION_RELEVANCE, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('classification.consultation')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_CONSULTATION_CLASSIFICATION}
              title={t('helptext')}
              initialValue={helpClassification?.CLASSIFICATION_CONSULTATION}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, CLASSIFICATION_BLOCKING_RECOMENDATION, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('classification.blockingRecommendation')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_BLOCKING_RECOMENDATION_CLASSIFICATION}
              title={t('helptext')}
              initialValue={helpClassification?.CLASSIFICATION_BLOCKING_RECOMENDATION}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, CLASSIFICATION_CONSULTATION, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('classification.chinesseMarket')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_CHINA_CLASSIFICATION}
              title={t('helptext')}
              initialValue={helpClassification?.CLASSIFICATION_AFFECTS_CHINA}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, CLASSIFICATION_AFFECTS_CHINA, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('classification.developmentVehicleAssessment')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_DEVELOPMENT_VEHICLE_ASSESSMENT_CLASSIFICATION}
              title={t('helptext')}
              initialValue={helpClassification?.CLASSIFICATION_DEVELOPMENT_VEHICLE_ASSESSMENT}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, CLASSIFICATION_DEVELOPMENT_VEHICLE_ASSESSMENT, newValue)}
            />
          </AccordionPanel>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  helpClassification: getHelpClassification(state),
});

const actionCreators = {
  setClassification: setHelpClassification,
};

export default connect(mapStateToProps, actionCreators)(ClassificationHelpText);
